import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
    Row,
    Col,
    Card,
    Button,
    Space,
    Tag,
    Descriptions,
    Alert,
    Divider,
    message,
    Spin,
    Modal,
    Upload,
    Tabs,
    Tooltip,
    Typography,
    Input,
    Form,
    Popconfirm,
    Empty,
    Badge
} from 'antd';
import {
    ApiOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    SyncOutlined,
    LinkOutlined,
    DisconnectOutlined,
    UploadOutlined,
    ShoppingCartOutlined,
    InboxOutlined,
    DollarOutlined,
    SettingOutlined,
    RocketOutlined,
    ShopOutlined,
    CloudSyncOutlined,
    CalculatorOutlined,
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    KeyOutlined,
    EyeInvisibleOutlined
} from '@ant-design/icons';
import { integrationsApi } from '../../api/client';

const { Text, Title, Paragraph } = Typography;
const { TabPane } = Tabs;

interface AllegroProfile {
    id: string;
    name: string;
    isActive: boolean;
    hasCredentials: boolean;
    connected: boolean;
    connectedAt?: string;
    lastSync?: string;
    login?: string;
    createdAt: string;
}

export default function IntegrationsPage() {
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editingProfile, setEditingProfile] = useState<AllegroProfile | null>(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();

    // Check for OAuth callback result
    useEffect(() => {
        const allegroStatus = searchParams.get('allegro');
        const channelId = searchParams.get('channelId');
        if (allegroStatus === 'connected') {
            message.success('Połączono z Allegro!');
            setSearchParams({});
            queryClient.invalidateQueries({ queryKey: ['allegro-profiles'] });
        } else if (allegroStatus === 'error') {
            message.error('Błąd połączenia z Allegro');
            setSearchParams({});
        }
    }, [searchParams, setSearchParams, queryClient]);

    // Query all Allegro profiles
    const { data: profiles = [], isLoading } = useQuery<AllegroProfile[]>({
        queryKey: ['allegro-profiles'],
        queryFn: () => integrationsApi.getAllegroProfiles().then((res: any) => res.data),
    });

    // ============ PROFILE CRUD MUTATIONS ============

    const createProfileMutation = useMutation({
        mutationFn: (data: { name: string; clientId: string; clientSecret: string; redirectUri?: string }) =>
            integrationsApi.createAllegroProfile(data),
        onSuccess: () => {
            message.success('Profil Allegro utworzony');
            queryClient.invalidateQueries({ queryKey: ['allegro-profiles'] });
            setAddModalOpen(false);
            addForm.resetFields();
        },
        onError: () => message.error('Błąd tworzenia profilu'),
    });

    const updateProfileMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) =>
            integrationsApi.updateAllegroProfile(id, data),
        onSuccess: () => {
            message.success('Profil zaktualizowany');
            queryClient.invalidateQueries({ queryKey: ['allegro-profiles'] });
            setEditingProfile(null);
            editForm.resetFields();
        },
        onError: () => message.error('Błąd aktualizacji profilu'),
    });

    const deleteProfileMutation = useMutation({
        mutationFn: (id: string) => integrationsApi.deleteAllegroProfile(id),
        onSuccess: () => {
            message.success('Profil usunięty');
            queryClient.invalidateQueries({ queryKey: ['allegro-profiles'] });
        },
        onError: () => message.error('Błąd usuwania profilu'),
    });

    // ============ PER-PROFILE ACTION MUTATIONS ============

    const disconnectMutation = useMutation({
        mutationFn: (channelId: string) => integrationsApi.disconnectAllegro(channelId),
        onSuccess: () => {
            message.success('Rozłączono z Allegro');
            queryClient.invalidateQueries({ queryKey: ['allegro-profiles'] });
        },
        onError: () => message.error('Błąd rozłączania'),
    });

    const importOrdersMutation = useMutation({
        mutationFn: ({ channelId, daysBack }: { channelId: string; daysBack?: number }) =>
            integrationsApi.importAllegroOrders(channelId, daysBack),
        onSuccess: (res: any) => message.success(res.data.message || 'Zamówienia zaimportowane'),
        onError: () => message.error('Błąd importu zamówień'),
    });

    const importSettlementsMutation = useMutation({
        mutationFn: (channelId: string) => integrationsApi.importAllegroSettlements(channelId),
        onSuccess: (res: any) => message.success(res.data.message || 'Rozliczenia zaimportowane'),
        onError: () => message.error('Błąd importu rozliczeń'),
    });

    const syncFulfillmentStockMutation = useMutation({
        mutationFn: (channelId: string) => integrationsApi.syncFulfillmentStock(channelId),
        onSuccess: (res: any) => message.success(res.data.message || 'Stany z Fulfillment zsynchronizowane'),
        onError: () => message.error('Błąd synchronizacji stanów z Fulfillment'),
    });

    const recalculateCostsMutation = useMutation({
        mutationFn: () => integrationsApi.recalculateCosts(),
        onSuccess: (res: any) => message.success(res.data.message || 'Przeliczono koszty zamówień'),
        onError: () => message.error('Błąd przeliczania kosztów'),
    });

    const importBillingFeesMutation = useMutation({
        mutationFn: ({ channelId, daysBack }: { channelId: string; daysBack?: number }) =>
            integrationsApi.importAllegroBillingFees(channelId, daysBack),
        onSuccess: (res: any) => message.success(res.data.message || 'Zaimportowano opłaty i prowizje'),
        onError: () => message.error('Błąd importu opłat'),
    });

    // CSV Import per profile
    const [csvImporting, setCsvImporting] = useState<string | null>(null);
    const handleCsvUpload = async (file: File, channelId: string) => {
        setCsvImporting(channelId);
        try {
            const text = await file.text();
            const response = await integrationsApi.importAllegroCSV(text, channelId);
            const result = (response as any).data;
            if (result.status === 'success') {
                message.success(result.message || 'Import CSV zakończony');
                queryClient.invalidateQueries({ queryKey: ['orders'] });
            } else {
                message.error(result.message || 'Błąd importu CSV');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Błąd importu CSV');
        } finally {
            setCsvImporting(null);
        }
        return false;
    };

    const handleConnect = (channelId: string) => {
        // Navigate to authorize endpoint with channelId
        const apiUrl = import.meta.env.VITE_API_URL || '';
        window.location.href = `${apiUrl}/api/integrations/allegro/authorize?channelId=${channelId}`;
    };

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: 50 }}>
                <Spin size="large" />
            </div>
        );
    }

    const activeProfiles = profiles.filter(p => p.isActive);

    // --- Render single profile card ---
    const renderProfileCard = (profile: AllegroProfile) => (
        <Card
            key={profile.id}
            title={
                <Space>
                    <ApiOutlined style={{ fontSize: 24, color: '#FF5A00' }} />
                    <span style={{ fontSize: 16, fontWeight: 600 }}>{profile.name}</span>
                    {profile.connected ? (
                        <Tag color="success" icon={<CheckCircleOutlined />}>Połączono</Tag>
                    ) : (
                        <Tag color="default" icon={<CloseCircleOutlined />}>Niepołączono</Tag>
                    )}
                </Space>
            }
            extra={
                <Space>
                    <Tooltip title="Edytuj profil">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => {
                                setEditingProfile(profile);
                                editForm.setFieldsValue({ name: profile.name });
                            }}
                        />
                    </Tooltip>
                    {profile.connected && (
                        <Popconfirm title="Czy na pewno chcesz rozłączyć?" onConfirm={() => disconnectMutation.mutate(profile.id)}>
                            <Button type="text" danger icon={<DisconnectOutlined />}>Rozłącz</Button>
                        </Popconfirm>
                    )}
                    {!profile.connected && (
                        <Popconfirm title="Czy na pewno chcesz usunąć ten profil?" onConfirm={() => deleteProfileMutation.mutate(profile.id)}>
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </Space>
            }
            style={{ marginBottom: 24 }}
        >
            {!profile.connected ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    {!profile.hasCredentials ? (
                        <Alert
                            message="Brak konfiguracji"
                            description="Ten profil nie ma skonfigurowanych danych dostępowych (Client ID / Client Secret). Edytuj profil, aby dodać dane."
                            type="warning"
                            showIcon
                            style={{ marginBottom: 24, maxWidth: 600, margin: '0 auto 24px' }}
                        />
                    ) : (
                        <Alert
                            message="Integracja nieaktywna"
                            description="Połącz swoje konto Allegro, aby automatycznie importować zamówienia, stany magazynowe i rozliczenia."
                            type="info"
                            showIcon
                            style={{ marginBottom: 24, maxWidth: 600, margin: '0 auto 24px' }}
                        />
                    )}
                    <Button
                        type="primary"
                        icon={<LinkOutlined />}
                        onClick={() => handleConnect(profile.id)}
                        size="large"
                        disabled={!profile.hasCredentials}
                    >
                        Połącz z Allegro
                    </Button>
                </div>
            ) : (
                <Row gutter={[24, 24]}>
                    <Col xs={24} md={6}>
                        <Card size="small" title="Informacje">
                            <Descriptions column={1} size="small">
                                <Descriptions.Item label="Połączono">
                                    {profile.connectedAt ? new Date(profile.connectedAt).toLocaleDateString() : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Ostatni sync">
                                    {profile.lastSync ? new Date(profile.lastSync).toLocaleString() : 'Nigdy'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Konto">
                                    {profile.login || 'Nieznane'}
                                </Descriptions.Item>
                            </Descriptions>
                            <Divider style={{ margin: '12px 0' }} />
                            <Text strong style={{ fontSize: 12 }}>Auto-synchronizacja:</Text>
                            <ul style={{ margin: '4px 0', paddingLeft: 16, fontSize: 12 }}>
                                <li><strong>Zamówienia:</strong> co 15 min</li>
                                <li><strong>Prowizje/opłaty:</strong> codziennie 5:00</li>
                                <li><strong>Rozliczenia:</strong> codziennie 6:00</li>
                                <li><strong>Stany Fulfillment:</strong> co godzinę</li>
                            </ul>
                        </Card>
                    </Col>

                    <Col xs={24} md={18}>
                        <Tabs defaultActiveKey="orders">
                            <TabPane
                                tab={<span><ShoppingCartOutlined /> Zamówienia</span>}
                                key="orders"
                            >
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Alert
                                        message="Synchronizacja zamówień"
                                        description="System automatycznie pobiera zamówienia co 15 minut."
                                        type="info" showIcon
                                        style={{ marginBottom: 16 }}
                                    />
                                    <Row gutter={[16, 16]}>
                                        <Col span={8}>
                                            <Button
                                                block type="primary" ghost icon={<SyncOutlined />}
                                                onClick={() => importOrdersMutation.mutate({ channelId: profile.id, daysBack: 30 })}
                                                loading={importOrdersMutation.isPending}
                                            >
                                                Importuj (30 dni)
                                            </Button>
                                        </Col>
                                        <Col span={8}>
                                            <Button
                                                block icon={<CloudSyncOutlined />}
                                                onClick={() => Modal.confirm({
                                                    title: 'Głęboki import',
                                                    content: 'Pobrać zamówienia z 90 dni?',
                                                    onOk: () => importOrdersMutation.mutate({ channelId: profile.id, daysBack: 90 })
                                                })}
                                                loading={importOrdersMutation.isPending}
                                            >
                                                Głęboki import (90 dni)
                                            </Button>
                                        </Col>
                                        <Col span={8}>
                                            <Upload
                                                accept=".csv"
                                                showUploadList={false}
                                                beforeUpload={(file) => handleCsvUpload(file, profile.id)}
                                                disabled={csvImporting === profile.id}
                                            >
                                                <Button block icon={<UploadOutlined />} loading={csvImporting === profile.id}>
                                                    Import z CSV
                                                </Button>
                                            </Upload>
                                        </Col>
                                    </Row>
                                </Space>
                            </TabPane>

                            <TabPane
                                tab={<span><InboxOutlined /> Magazyn</span>}
                                key="stock"
                            >
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Paragraph>
                                        Integracja sprawdza stany magazynowe w <strong>One Fulfillment by Allegro</strong> i tworzy dla nich dedykowaną lokalizację <code>AFN-01</code>.
                                    </Paragraph>
                                    <Button
                                        type="primary" icon={<SyncOutlined />}
                                        onClick={() => syncFulfillmentStockMutation.mutate(profile.id)}
                                        loading={syncFulfillmentStockMutation.isPending}
                                    >
                                        Synchronizuj stany Fulfillment
                                    </Button>
                                </Space>
                            </TabPane>

                            <TabPane
                                tab={<span><DollarOutlined /> Finanse</span>}
                                key="finance"
                            >
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Divider orientation="left">Ręczny import prowizji i opłat</Divider>
                                    <Row gutter={[12, 12]}>
                                        <Col span={6}>
                                            <Button
                                                block icon={<DollarOutlined />}
                                                onClick={() => importBillingFeesMutation.mutate({ channelId: profile.id, daysBack: 30 })}
                                                loading={importBillingFeesMutation.isPending}
                                            >
                                                Ostatnie 30 dni
                                            </Button>
                                        </Col>
                                        <Col span={6}>
                                            <Button
                                                block icon={<DollarOutlined />}
                                                onClick={() => importBillingFeesMutation.mutate({ channelId: profile.id, daysBack: 90 })}
                                                loading={importBillingFeesMutation.isPending}
                                            >
                                                Ostatnie 90 dni
                                            </Button>
                                        </Col>
                                        <Col span={6}>
                                            <Button
                                                block icon={<DollarOutlined />}
                                                onClick={() => importBillingFeesMutation.mutate({ channelId: profile.id, daysBack: 365 })}
                                                loading={importBillingFeesMutation.isPending}
                                            >
                                                Ostatni rok
                                            </Button>
                                        </Col>
                                        <Col span={6}>
                                            <Button
                                                block type="primary" icon={<CloudSyncOutlined />}
                                                onClick={() => Modal.confirm({
                                                    title: 'Pobierz wszystkie opłaty',
                                                    content: 'Pobierze opłaty i prowizje z ostatnich 3 lat. Może to chwilę potrwać.',
                                                    onOk: () => importBillingFeesMutation.mutate({ channelId: profile.id, daysBack: 1095 })
                                                })}
                                                loading={importBillingFeesMutation.isPending}
                                            >
                                                Pobierz wszystko
                                            </Button>
                                        </Col>
                                    </Row>
                                    <Divider orientation="left">Inne operacje</Divider>
                                    <Row gutter={[12, 12]}>
                                        <Col span={12}>
                                            <Button
                                                block icon={<DollarOutlined />}
                                                onClick={() => importSettlementsMutation.mutate(profile.id)}
                                                loading={importSettlementsMutation.isPending}
                                            >
                                                Pobierz Billing (Rozliczenia)
                                            </Button>
                                        </Col>
                                        <Col span={12}>
                                            <Tooltip title="Wymusza przeliczenie marży dla WSZYSTKICH zamówień na podstawie aktualnych cen zakupu produktów">
                                                <Button
                                                    block danger type="dashed" icon={<CalculatorOutlined />}
                                                    onClick={() => Modal.confirm({
                                                        title: 'Przeliczyć koszty?',
                                                        content: 'Ta operacja sprawdzi wszystkie zamówienia w systemie i zaktualizuje ich koszty (COGS) na podstawie aktualnych cen zakupu produktów w bazie. Może to potrwać chwilę.',
                                                        onOk: () => recalculateCostsMutation.mutate()
                                                    })}
                                                    loading={recalculateCostsMutation.isPending}
                                                >
                                                    Wymuś przeliczenie COGS
                                                </Button>
                                            </Tooltip>
                                        </Col>
                                    </Row>
                                </Space>
                            </TabPane>
                        </Tabs>
                    </Col>
                </Row>
            )}
        </Card>
    );

    const renderAvailableIntegrations = () => (
        <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
                <Card hoverable style={{ opacity: 0.6 }}>
                    <Card.Meta
                        avatar={<RocketOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                        title="BaseLinker"
                        description="Wkrótce: Integracja z BaseLinkerem do obsługi wielu marketplace'ów."
                    />
                    <div style={{ marginTop: 16 }}>
                        <Button disabled size="small">Wkrótce</Button>
                    </div>
                </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
                <Card hoverable style={{ opacity: 0.6 }}>
                    <Card.Meta
                        avatar={<ShopOutlined style={{ fontSize: 24, color: '#52c41a' }} />}
                        title="PrestaShop"
                        description="Wkrótce: Synchronizacja produktów i zamówień ze sklepem."
                    />
                    <div style={{ marginTop: 16 }}>
                        <Button disabled size="small">Wkrótce</Button>
                    </div>
                </Card>
            </Col>
        </Row>
    );

    // --- Add Profile Modal ---
    const renderAddModal = () => (
        <Modal
            title={
                <Space>
                    <PlusOutlined />
                    <span>Dodaj profil Allegro</span>
                </Space>
            }
            open={addModalOpen}
            onCancel={() => { setAddModalOpen(false); addForm.resetFields(); }}
            onOk={() => addForm.submit()}
            okText="Utwórz profil"
            cancelText="Anuluj"
            confirmLoading={createProfileMutation.isPending}
        >
            <Form
                form={addForm}
                layout="vertical"
                onFinish={(values) => createProfileMutation.mutate(values)}
            >
                <Form.Item
                    name="name"
                    label="Nazwa profilu"
                    rules={[{ required: true, message: 'Podaj nazwę profilu' }]}
                >
                    <Input placeholder="np. Allegro CureCraft" />
                </Form.Item>
                <Alert
                    message="Dane z panelu deweloperskiego Allegro"
                    description="Client ID i Client Secret znajdziesz w panelu apps.developer.allegro.pl po utworzeniu aplikacji."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Form.Item
                    name="clientId"
                    label="Client ID"
                    rules={[{ required: true, message: 'Podaj Client ID' }]}
                >
                    <Input placeholder="np. a1b2c3d4e5f6..." prefix={<KeyOutlined />} />
                </Form.Item>
                <Form.Item
                    name="clientSecret"
                    label="Client Secret"
                    rules={[{ required: true, message: 'Podaj Client Secret' }]}
                >
                    <Input.Password placeholder="Client Secret" prefix={<EyeInvisibleOutlined />} />
                </Form.Item>
                <Form.Item
                    name="redirectUri"
                    label="Redirect URI (opcjonalny)"
                    help="Pozostaw puste, aby użyć domyślnego z konfiguracji serwera"
                >
                    <Input placeholder="np. http://your-server/api/integrations/allegro/callback" />
                </Form.Item>
            </Form>
        </Modal>
    );

    // --- Edit Profile Modal ---
    const renderEditModal = () => (
        <Modal
            title={
                <Space>
                    <EditOutlined />
                    <span>Edytuj profil: {editingProfile?.name}</span>
                </Space>
            }
            open={!!editingProfile}
            onCancel={() => { setEditingProfile(null); editForm.resetFields(); }}
            onOk={() => editForm.submit()}
            okText="Zapisz"
            cancelText="Anuluj"
            confirmLoading={updateProfileMutation.isPending}
        >
            <Form
                form={editForm}
                layout="vertical"
                onFinish={(values) => {
                    if (editingProfile) {
                        // Only send non-empty values
                        const data: any = {};
                        if (values.name) data.name = values.name;
                        if (values.clientId) data.clientId = values.clientId;
                        if (values.clientSecret) data.clientSecret = values.clientSecret;
                        if (values.redirectUri) data.redirectUri = values.redirectUri;
                        updateProfileMutation.mutate({ id: editingProfile.id, data });
                    }
                }}
            >
                <Form.Item
                    name="name"
                    label="Nazwa profilu"
                >
                    <Input placeholder="np. Allegro CureCraft" />
                </Form.Item>
                <Divider orientation="left" style={{ fontSize: 12 }}>Zmiana danych dostępowych (opcjonalnie)</Divider>
                <Form.Item
                    name="clientId"
                    label="Nowy Client ID"
                    help="Pozostaw puste, aby nie zmieniać"
                >
                    <Input placeholder="Nowy Client ID" prefix={<KeyOutlined />} />
                </Form.Item>
                <Form.Item
                    name="clientSecret"
                    label="Nowy Client Secret"
                    help="Pozostaw puste, aby nie zmieniać"
                >
                    <Input.Password placeholder="Nowy Client Secret" prefix={<EyeInvisibleOutlined />} />
                </Form.Item>
                <Form.Item
                    name="redirectUri"
                    label="Nowy Redirect URI"
                    help="Pozostaw puste, aby nie zmieniać"
                >
                    <Input placeholder="Nowy Redirect URI" />
                </Form.Item>
            </Form>
        </Modal>
    );

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Title level={2}>Integracje</Title>

            <Tabs defaultActiveKey="active" type="card">
                <TabPane tab={<span>Aktywne {activeProfiles.length > 0 && <Badge count={activeProfiles.length} style={{ marginLeft: 8 }} />}</span>} key="active">
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {activeProfiles.map(profile => renderProfileCard(profile))}

                        {activeProfiles.length === 0 && (
                            <Empty
                                description="Brak aktywnych integracji"
                                style={{ padding: '40px 0' }}
                            >
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setAddModalOpen(true)}
                                >
                                    Dodaj profil Allegro
                                </Button>
                            </Empty>
                        )}

                        {/* Add profile button */}
                        <Card
                            hoverable
                            style={{
                                borderStyle: 'dashed',
                                textAlign: 'center',
                                padding: '16px 0',
                                cursor: 'pointer',
                            }}
                            onClick={() => setAddModalOpen(true)}
                        >
                            <Space direction="vertical" align="center">
                                <PlusOutlined style={{ fontSize: 28, color: '#1890ff' }} />
                                <Text type="secondary">Dodaj nowy profil Allegro</Text>
                            </Space>
                        </Card>
                    </Space>
                </TabPane>
                <TabPane tab="Dostępne" key="available">
                    {renderAvailableIntegrations()}
                </TabPane>
            </Tabs>

            {renderAddModal()}
            {renderEditModal()}
        </Space>
    );
}
